import { m, useScroll } from 'framer-motion';
import { useRef, useState, useEffect, useCallback } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Unstable_Grid2';
import Typography from '@mui/material/Typography';
import { alpha, styled, useTheme } from '@mui/material/styles';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { useResponsive } from 'src/hooks/use-responsive';

import { HEADER } from 'src/layouts/config-layout';
import { bgBlur, bgGradient, textGradient } from 'src/theme/css';

import { varFade, MotionContainer } from 'src/components/animate';
import { useAuthContext } from 'src/auth/hooks';
import { signupUrl } from 'src/config-global';

// ----------------------------------------------------------------------

const StyledRoot = styled('div')(({ theme }) => ({
  ...bgGradient({
    color: alpha(theme.palette.background.default, theme.palette.mode === 'light' ? 0.9 : 0.94),
    imgUrl: '/assets/background/overlay_3.jpg',
  }),
  width: '100%',
  height: '100vh',
  position: 'relative',
  [theme.breakpoints.up('md')]: {
    top: 0,
    left: 0,
    position: 'fixed',
  },
}));

const StyledWrapper = styled('div')(({ theme }) => ({
  height: '100%',
  overflow: 'hidden',
  position: 'relative',
  [theme.breakpoints.up('md')]: {
    marginTop: HEADER.H_DESKTOP_OFFSET,
  },
}));

const StyledTextGradient = styled(m.h1)(({ theme }) => ({
  ...textGradient(
    `300deg, #00C2FF 0%, #1025E4 25%, #00C2FF 50%, #1025E4 75%, #00C2FF 100%`
  ),
  margin: 0,
  padding: 0,
  lineHeight: 1.05,
  fontWeight: 900,
  letterSpacing: 1,
  backgroundSize: '400%',
  fontSize: `${44 / 16}rem`,
  fontFamily: theme.typography.fontSecondaryFamily,
  [theme.breakpoints.up('md')]: {
    fontSize: `${64 / 16}rem`,
  },
}));

const StyledEllipseTop = styled('div')(({ theme }) => ({
  top: -80,
  width: 480,
  right: -80,
  height: 480,
  borderRadius: '50%',
  position: 'absolute',
  filter: 'blur(100px)',
  WebkitFilter: 'blur(100px)',
  backgroundColor: alpha(theme.palette.primary.darker, 0.12),
}));

const StyledEllipseBottom = styled('div')(({ theme }) => ({
  height: 400,
  bottom: -200,
  left: '10%',
  right: '10%',
  borderRadius: '50%',
  position: 'absolute',
  filter: 'blur(100px)',
  WebkitFilter: 'blur(100px)',
  backgroundColor: alpha(theme.palette.primary.darker, 0.12),
}));

type StyledPolygonProps = {
  opacity?: number;
  anchor?: 'left' | 'right';
};

const StyledPolygon = styled('div')<StyledPolygonProps>(
  ({ opacity = 1, anchor = 'left', theme }) => ({
    ...bgBlur({
      opacity,
      color: theme.palette.background.default,
    }),
    zIndex: 9,
    bottom: 0,
    height: 80,
    width: '50%',
    position: 'absolute',
    clipPath: 'polygon(0% 0%, 100% 100%, 0% 100%)',
    ...(anchor === 'left' && {
      left: 0,
      ...(theme.direction === 'rtl' && {
        transform: 'scale(-1, 1)',
      }),
    }),
    ...(anchor === 'right' && {
      right: 0,
      transform: 'scaleX(-1)',
      ...(theme.direction === 'rtl' && {
        transform: 'scaleX(1)',
      }),
    }),
  })
);

// ----------------------------------------------------------------------

export default function HomeHero() {
  const mdUp = useResponsive('up', 'md');

  const theme = useTheme();

  const heroRef = useRef<HTMLDivElement | null>(null);

  const { scrollY } = useScroll();

  const [percent, setPercent] = useState(0);

  const { user } = useAuthContext();

  const lightMode = theme.palette.mode === 'light';

  const getScroll = useCallback(() => {
    let heroHeight = 0;

    if (heroRef.current) {
      heroHeight = heroRef.current.offsetHeight;
    }

    scrollY.on('change', (scrollHeight: number) => {
      const scrollPercent = (scrollHeight * 100) / heroHeight;

      setPercent(Math.floor(scrollPercent));
    });
  }, [scrollY]);

  useEffect(() => {
    getScroll();
  }, [getScroll]);

  const opacity = 1 - percent / 100;

  const hide = percent > 120;

  const renderDescription = (
    <Stack
      spacing={3}
      alignItems={{ xs: 'center', md: 'flex-start' }}
      sx={{
        maxWidth: { md: 520 },
        mx: { xs: 'auto', md: 0 },
        textAlign: { xs: 'center', md: 'left' },
        opacity: opacity > 0 ? opacity : 0,
      }}
    >
      <m.div variants={varFade().inUp}>
        <Typography variant="h2" sx={{ lineHeight: 1.15 }}>
          Level up your team&apos;s skills with
        </Typography>
      </m.div>

      <m.div variants={varFade().inUp}>
        <StyledTextGradient
          animate={{ backgroundPosition: '200% center' }}
          transition={{
            repeatType: 'reverse',
            ease: 'linear',
            duration: 20,
            repeat: Infinity,
          }}
        >
          TechStack
        </StyledTextGradient>
      </m.div>

      <m.div variants={varFade().inUp}>
        <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 440 }}>
          Track skills, close gaps, and grow your team - all in one place.
        </Typography>
      </m.div>

      <m.div variants={varFade().inUp}>
        <Stack sx={{ pt: 1, width: { xs: 1, sm: 'auto' } }}>
          <Button
            component={RouterLink}
            href={user ? paths.dashboard.root : signupUrl}
            color="primary"
            size="large"
            variant="contained"
            sx={{ px: 3, width: { xs: 1, sm: 'auto' } }}
          >
            {user ? 'Go to dashboard' : 'Get started free'}
          </Button>
        </Stack>
      </m.div>
    </Stack>
  );

  const renderImage = (
    <Box
      sx={{
        position: 'relative',
        perspective: '2400px',
        opacity: opacity > 0 ? opacity : 0,
      }}
    >
      {/* soft glow behind the window for depth */}
      <Box
        sx={{
          position: 'absolute',
          inset: '6% -8% 6% 6%',
          borderRadius: '50%',
          filter: 'blur(120px)',
          bgcolor: (t) => alpha(t.palette.primary.main, 0.18),
        }}
      />

      <Box
        component={m.div}
        initial={false}
        animate={{ y: [-8, 8, -8] }}
        transition={{ duration: 9, ease: 'easeInOut', repeat: Infinity }}
        sx={{ position: 'relative' }}
      >
        <Box
          sx={{
            overflow: 'hidden',
            borderRadius: 2.5,
            bgcolor: 'background.paper',
            transformStyle: 'preserve-3d',
            transformOrigin: 'center',
            transform: { md: 'rotateY(-12deg) rotate(1.5deg)' },
            border: (t) => `solid 1px ${alpha(t.palette.grey[500], 0.16)}`,
            boxShadow: (t) =>
              `-40px 50px 100px -20px ${
                lightMode
                  ? alpha(t.palette.grey[500], 0.3)
                  : alpha(theme.palette.common.black, 0.45)
              }`,
            transition: (t) =>
              t.transitions.create('transform', {
                duration: t.transitions.duration.complex,
              }),
            '&:hover': { transform: { md: 'rotateY(0deg) rotate(0deg)' } },
          }}
        >
          <Box
            sx={{
              px: 2,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              gap: 0.875,
              borderBottom: (t) => `solid 1px ${alpha(t.palette.grey[500], 0.12)}`,
              bgcolor: (t) => alpha(t.palette.grey[500], 0.06),
            }}
          >
            {['#FF5F57', '#FEBC2E', '#28C840'].map((c) => (
              <Box key={c} sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: c }} />
            ))}
          </Box>

          <Box
            component="img"
            alt="TechStack dashboard"
            src="/assets/images/home/dashboard.png"
            sx={{ display: 'block', width: 1, objectFit: 'cover' }}
          />
        </Box>
      </Box>
    </Box>
  );

  const renderPolygons = (
    <>
      <StyledPolygon />
      <StyledPolygon anchor="right" opacity={0.48} />
      <StyledPolygon anchor="right" opacity={0.48} sx={{ height: 48, zIndex: 10 }} />
      <StyledPolygon anchor="right" sx={{ zIndex: 11, height: 24 }} />
    </>
  );

  const renderEllipses = (
    <>
      {mdUp && <StyledEllipseTop />}
      <StyledEllipseBottom />
    </>
  );

  return (
    <>
      <StyledRoot
        ref={heroRef}
        sx={{
          ...(hide && {
            opacity: 0,
          }),
        }}
      >
        <StyledWrapper>
          <Container
            component={MotionContainer}
            sx={{ height: 1, position: 'relative', zIndex: 8 }}
          >
            <Grid
              container
              spacing={{ xs: 6, md: 8 }}
              alignItems="center"
              sx={{ height: 1 }}
            >
              <Grid
                xs={12}
                md={7}
                sx={{ order: { xs: 2, md: 1 }, display: { xs: 'none', md: 'block' } }}
              >
                {renderImage}
              </Grid>

              <Grid xs={12} md={5} sx={{ order: { xs: 1, md: 2 } }}>
                {renderDescription}
              </Grid>
            </Grid>
          </Container>

          {renderEllipses}
        </StyledWrapper>
      </StyledRoot>

      {mdUp && renderPolygons}

      <Box sx={{ height: { md: '100vh' } }} />
    </>
  );
}
